/**
 * Settings that belong to the phone rather than to the tournament.
 *
 * Notifications are a permission granted by one device to one browser, and
 * unsent scores are bytes sitting in one browser's storage. Neither syncs,
 * neither is an admin's business, and neither has any meaning on anybody else's
 * handset — so they live here, outside the Setup PIN, which guards the shared
 * document.
 */

import { useEffect, useState } from 'react'
import { disablePush, enablePush, pushState, type PushState } from '../push'
import { call, flush, logout, useStore } from '../store'
import { applyUpdate, buildLabel, checkForUpdate, type UpdateState } from '../version'
import type { Tournament } from '../engine/types'
import { SectionRule } from '../ui'

function Row({
  title,
  detail,
  action,
}: {
  title: string
  detail: string
  action?: React.ReactNode
}) {
  return (
    <div className="px-4 py-3 border-b border-rule">
      <div className="flex items-baseline justify-between gap-3">
        <span className="bc-name text-sm">{title}</span>
        {action}
      </div>
      <p className="text-[13px] text-mute leading-snug mt-1">{detail}</p>
    </div>
  )
}

function Notifications() {
  const [state, setState] = useState<PushState | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tested, setTested] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    pushState().then((s) => live && setState(s))
    return () => {
      live = false
    }
  }, [])

  const toggle = async () => {
    setBusy(true)
    setError(null)
    try {
      setState(state === 'on' ? await disablePush() : await enablePush())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change notifications')
    } finally {
      setBusy(false)
    }
  }

  const test = async () => {
    setBusy(true)
    setError(null)
    setTested(null)
    try {
      const r = await call<{ sent: number; devices: number }>('push/test', { method: 'POST' })
      setTested(
        r.sent > 0
          ? `Sent to ${r.sent} device${r.sent === 1 ? '' : 's'}. If nothing appeared, notifications are muted in the phone's own settings.`
          : 'The server accepted it but no device took it. The subscription has probably expired — turn alerts off and on again.',
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send a test')
    } finally {
      setBusy(false)
    }
  }

  if (state === null) return null

  if (state === 'needs-install') {
    return (
      <Row
        title="Birdie alerts"
        detail="iPhone only sends these to an app on the home screen. Tap Share, then “Add to Home Screen”, open it from there, and this will turn on."
      />
    )
  }
  if (state === 'unavailable') {
    return <Row title="Birdie alerts" detail="This browser cannot receive notifications." />
  }
  if (state === 'blocked') {
    return (
      <Row
        title="Birdie alerts"
        detail="Notifications are blocked for this app. That can only be undone in the phone's own settings — the app is not allowed to ask again."
      />
    )
  }

  return (
    <>
      <Row
        title="Birdie alerts"
        detail={
          state === 'on'
            ? 'On. Everyone with alerts on hears about a birdie or better, about half a minute after it is entered.'
            : 'Off. Turn these on to hear when anyone makes a birdie or better.'
        }
        action={
          <button
            type="button"
            onClick={toggle}
            disabled={busy}
            className={`bc-tap bc-label px-4 py-2 disabled:opacity-40 ${
              state === 'on' ? 'border-2 border-ink' : 'bg-signal text-paper'
            }`}
          >
            {busy ? '…' : state === 'on' ? 'Turn off' : 'Turn on'}
          </button>
        }
      />
      {error && <p className="bg-signal text-paper px-4 py-2 text-xs">{error}</p>}
      {state === 'on' && (
        <>
          <Row
            title="Send a test"
            detail={
              tested ??
              'Everything between the app and your lock screen fails silently. This is the only way to know it works without waiting for someone to make a birdie.'
            }
            action={
              <button
                type="button"
                onClick={test}
                disabled={busy}
                className="bc-tap bc-label px-4 py-2 border-2 border-ink disabled:opacity-40"
              >
                {busy ? '…' : 'Test'}
              </button>
            }
          />
          <p className="px-4 py-2 text-[13px] text-mute leading-snug">
            There is a short delay on purpose. A score can be corrected in the seconds
            after it is entered, and an announcement cannot be taken back — so the app
            waits for the number to settle and checks it again before telling anyone.
          </p>
        </>
      )}
    </>
  )
}

/** What this phone is still holding, and what it is doing about it. */
function Unsent() {
  const pending = useStore((s) => s.outbox.length)
  const online = useStore((s) => s.online && s.reachable)
  const lastSyncAt = useStore((s) => s.lastSyncAt)

  if (pending === 0) {
    return (
      <Row
        title="Scores on this phone"
        detail={
          lastSyncAt
            ? `Everything entered here has reached the server. Last checked ${new Date(lastSyncAt).toLocaleTimeString()}.`
            : 'Nothing waiting to send.'
        }
      />
    )
  }

  return (
    <Row
      title={`${pending} score${pending === 1 ? '' : 's'} waiting to send`}
      detail={
        online
          ? 'Sending now. These are saved on this phone and will not be lost.'
          : 'Saved on this phone. They go up by themselves the moment there is signal — there is nothing to do and nothing to lose by closing the app.'
      }
      action={
        online ? (
          <button
            type="button"
            onClick={() => void flush()}
            className="bc-tap bc-label px-4 py-2 border-2 border-ink"
          >
            Send now
          </button>
        ) : undefined
      }
    />
  )
}

/**
 * Who this phone thinks you are, and a way out of being wrong.
 *
 * The realistic failure is picking the wrong name in a car park, and until now
 * that was permanent on that handset — the sign-in screen never came back, so
 * every score entered from it was attributed to somebody else for the week.
 *
 * Switching drops the session, which also drops anything the outbox has not
 * delivered. That is fine seconds after signing in and ruinous four hours
 * later, so it refuses while there is unsent scoring and says why.
 */
function WhoAmI({ doc }: { doc: Tournament | null }) {
  const session = useStore((s) => s.session)
  /*
   * Every queue, not just the scores. Sign-out drops three outboxes, and the
   * guard counted one of them — so a struck Nassau or an unsent sign-off was
   * destroyed by a tap the guard had just declared safe. A queued sign-off is
   * the worse loss: the card LOOKS signed on this phone until the moment the
   * queue dies with the session.
   */
  const pending = useStore(
    (s) => s.outbox.length + s.confirmOutbox.length + s.sideOutbox.length,
  )
  const online = useStore((s) => s.online && s.reachable)
  const [asking, setAsking] = useState(false)
  if (!session) return null

  const me =
    doc?.players.find((p) => p.id === session.playerId)?.displayName ?? session.playerId

  return (
    <Row
      title={`Signed in as ${me}`}
      detail={
        pending > 0
          ? `${pending} unsent write${pending === 1 ? '' : 's'} on this phone — scores, sign-offs or side matches. Switching would lose ${pending === 1 ? 'it' : 'them'}; send ${pending === 1 ? 'it' : 'them'} first.`
          : asking
            ? 'Tap Sign out to go back to the picker. Nothing is lost — the scores live on the server.'
            : 'Wrong name, or want a different tournament? Both are on the sign-in screen.'
      }
      action={
        pending > 0 ? (
          <button
            type="button"
            onClick={() => void flush()}
            disabled={!online}
            className="bc-tap bc-label px-4 py-2 border-2 border-ink disabled:opacity-40"
          >
            {online ? 'Send now' : 'No signal'}
          </button>
        ) : asking ? (
          <button
            type="button"
            onClick={() => logout()}
            className="bc-tap bc-label px-4 py-2 bg-signal text-paper"
          >
            Sign out
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setAsking(true)}
            className="bc-tap bc-label px-4 py-2 border-2 border-ink"
          >
            Not you?
          </button>
        )
      }
    />
  )
}

/**
 * Which build this phone is running, and whether it is the current one.
 *
 * "It should be fixed" and "your phone has the fix" are different claims, and
 * on a property where the service worker caches the shell and deploys reach
 * edge nodes minutes apart, they are routinely both true and both wrong. This
 * makes the difference checkable rather than arguable.
 */
function Version() {
  const [state, setState] = useState<UpdateState>('unknown')
  const [checking, setChecking] = useState(true)

  const look = async () => {
    setChecking(true)
    setState(await checkForUpdate())
    setChecking(false)
  }

  useEffect(() => {
    void look()
  }, [])

  return (
    <Row
      title="App version"
      detail={
        checking
          ? `${buildLabel()} — checking…`
          : state === 'stale'
            ? `${buildLabel()} — a newer build is available.`
            : state === 'current'
              ? `${buildLabel()} — up to date.`
              : `${buildLabel()} — could not check just now.`
      }
      action={
        state === 'stale' ? (
          <button
            type="button"
            onClick={() => void applyUpdate()}
            className="bc-tap bc-label px-4 py-2 bg-signal text-paper"
          >
            Update
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void look()}
            disabled={checking}
            className="bc-tap bc-label px-4 py-2 border-2 border-ink disabled:opacity-40"
          >
            Check
          </button>
        )
      }
    />
  )
}

/**
 * The link to put in the group thread.
 *
 * Lived at the top of The week until Scott moved it here: it is used a
 * handful of times a season and was spending prime real estate on a screen
 * read forty times a day. No admin rights needed — sharing the week is
 * everybody's to do.
 *
 * `/t/<id>` rather than the bare domain, so it still means this year in two
 * years' time — and so it unfurls with the name, the venue and the dates
 * instead of with whatever tournament happens to be newest when it is tapped.
 */
export function ShareWeek({ doc }: { doc: Tournament }) {
  const [copied, setCopied] = useState(false)
  const url = `${location.origin}/t/${encodeURIComponent(doc.id)}`
  const line = `${doc.name} · ${doc.venue}`

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: doc.name, text: line, url })
        return
      }
    } catch {
      // Canceled, or refused. Fall through and copy instead.
    }
    try {
      await navigator.clipboard.writeText(`${line}\n${url}`)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // No clipboard either — nothing useful left to try.
    }
  }

  return (
    <button
      type="button"
      onClick={share}
      className="bc-tap w-full border-b border-rule bc-label py-3 text-left px-4 flex items-center justify-between"
    >
      <span>{copied ? 'Link copied' : 'Share the week'}</span>
      <span className="text-mute text-[13px] normal-case tracking-normal">
        {copied ? '' : 'tee times, foursomes, matchups'}
      </span>
    </button>
  )
}

export function Device({ doc }: { doc?: Tournament | null }) {
  return (
    <>
      <SectionRule>This phone</SectionRule>
      <WhoAmI doc={doc ?? null} />
      {doc && <ShareWeek doc={doc} />}
      <Notifications />
      <Unsent />
      <Version />
    </>
  )
}
